import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Icon, Spin } from 'antd';
import decode from 'jwt-decode';
import { SUPER_HOST, ADMIN } from '../Common/constants';
import { getPrimaryRole } from '../../utils/rolePriority';
import { fetchSpacesCounts } from './HostDashboardActions';
import '../../assets/scss/SpaceCountCards.scss';

const SpaceCountCards = ({ queryParams = {} }) => {
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state?.auth?.loginDetails?.data?.token);
  const spaceCounts = useSelector((state) => state?.hostDashboard?.data?.spaceCounts);

  const userType = authToken ? getPrimaryRole(decode(authToken)?.authorities) : null;

  useEffect(() => {
    if (authToken) {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': authToken,
      };
      if (userType === SUPER_HOST || userType === ADMIN) {
        dispatch(fetchSpacesCounts(headers, { ...queryParams, scope: 'all' }));
        dispatch(fetchSpacesCounts(headers, { ...queryParams, scope: 'own' }));
        dispatch(fetchSpacesCounts(headers, { ...queryParams, scope: 'managed' }));
      } else {
        dispatch(fetchSpacesCounts(headers, { ...queryParams, scope: 'own' }));
      }
    }
  }, [dispatch, authToken, userType, JSON.stringify(queryParams)]);

  const isMultiScope = userType === SUPER_HOST || userType === ADMIN;

  const isLoading = isMultiScope
    ? (!spaceCounts?.all || !spaceCounts?.own || !spaceCounts?.managed)
    : !spaceCounts?.own;

  const allCounts = spaceCounts?.all || { TOTAL: 0, ACTIVE: 0, INACTIVE: 0, PENDING: 0, DECLINED: 0, DRAFT: 0, DELETED: 0 };
  const ownCounts = spaceCounts?.own || { TOTAL: 0, ACTIVE: 0, INACTIVE: 0, PENDING: 0, DECLINED: 0, DRAFT: 0, DELETED: 0 };
  const managedCounts = spaceCounts?.managed || { TOTAL: 0, ACTIVE: 0, INACTIVE: 0, PENDING: 0, DECLINED: 0, DRAFT: 0, DELETED: 0 };

  const counts = isMultiScope ? allCounts : ownCounts;

  const cardsData = [
    {
      key: 'total',
      label: 'Total Spaces',
      value: counts.TOTAL,
      ownValue: ownCounts.TOTAL,
      managedValue: managedCounts.TOTAL,
      icon: 'shop',
      iconStyle: { backgroundColor: 'rgba(24, 144, 255, 0.1)', color: '#1890ff' }
    },
    {
      key: 'active',
      label: 'Active',
      value: counts.ACTIVE,
      ownValue: ownCounts.ACTIVE,
      managedValue: managedCounts.ACTIVE,
      icon: 'check-circle',
      iconStyle: { backgroundColor: 'rgba(82, 196, 26, 0.1)', color: '#52c41a' }
    },
    {
      key: 'pending',
      label: 'Pending',
      value: counts.PENDING,
      ownValue: ownCounts.PENDING,
      managedValue: managedCounts.PENDING,
      icon: 'clock-circle',
      iconStyle: { backgroundColor: 'rgba(250, 173, 20, 0.1)', color: '#faad14' }
    },
    {
      key: 'inactive',
      label: 'Inactive',
      value: counts.INACTIVE,
      ownValue: ownCounts.INACTIVE,
      managedValue: managedCounts.INACTIVE,
      icon: 'exclamation-circle',
      iconStyle: { backgroundColor: 'rgba(114, 46, 209, 0.1)', color: '#722ed1' }
    },
    {
      key: 'declined',
      label: 'Declined',
      value: counts.DECLINED,
      ownValue: ownCounts.DECLINED,
      managedValue: managedCounts.DECLINED,
      icon: 'close-circle',
      iconStyle: { backgroundColor: 'rgba(255, 77, 79, 0.1)', color: '#ff4d4f' }
    },
  ];

  if (userType === ADMIN) {
    cardsData.push(
      {
        key: 'draft',
        label: 'Draft',
        value: counts.DRAFT,
        ownValue: ownCounts.DRAFT,
        managedValue: managedCounts.DRAFT,
        icon: 'file-text',
        iconStyle: { backgroundColor: 'rgba(108, 92, 231, 0.1)', color: '#6c5ce7' }
      },
      {
        key: 'deleted',
        label: 'Deleted',
        value: counts.DELETED,
        ownValue: ownCounts.DELETED,
        managedValue: managedCounts.DELETED,
        icon: 'delete',
        iconStyle: { backgroundColor: 'rgba(144, 144, 144, 0.1)', color: '#909090' }
      }
    );
  }

  return (
    <div className="space-count-section" style={{ width: '100%', marginBottom: '10px' }}>
      <Spin spinning={isLoading} indicator={<Icon type="loading" style={{ fontSize: 24 }} spin />}>
        <div className={`analytics-grid ${userType === ADMIN ? 'cols-7' : 'cols-5'}`} style={{ marginBottom: '20px', width: '100%' }}>
          {cardsData.map((card) => (
            <div key={card.key} className="stat-card">
              <div className="stat-content">
                <span className="stat-title">{card.label}</span>
                <div className="stat-value">{card.value}</div>
                {isMultiScope && (
                  <>
                    {/* Commented out for admin view as it is not needed
                    userType === ADMIN && (
                      <div className="stat-subtext" style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '4px' }}>
                        Own: <strong>{card.ownValue}</strong> | System: <strong>{card.managedValue}</strong>
                      </div>
                    )
                    */}
                    {userType !== ADMIN && (
                      <div className="stat-subtext" style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '4px' }}>
                        Own: <strong>{card.ownValue}</strong> | {userType === ADMIN ? 'System' : 'Managed'}: <strong>{card.managedValue}</strong>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="stat-icon-wrapper" style={card.iconStyle}>
                <Icon type={card.icon} />
              </div>
            </div>
          ))}
        </div>
      </Spin>
    </div>
  );
};

export default SpaceCountCards;
